import { z } from 'zod';
// --- Element schemas ---
const positionSchema = z.object({
    x: z.number(),
    y: z.number(),
    width: z.number().positive(),
    height: z.number().positive(),
});
const elementStylesSchema = z.object({
    opacity: z.number().min(0).max(1),
    zIndex: z.number().int(),
    transform: z.string().optional(),
    borderRadius: z.number().optional(),
    // Epic 20 Phase 01 — per-corner radius (additive; `borderRadius` stays for the
    // uniform case). When present the renderer emits a 4-value `border-radius` that
    // takes precedence over the uniform value (ellipse 50% still wins above both).
    borderRadiusCorners: z
        .object({
        topLeft: z.number(),
        topRight: z.number(),
        bottomRight: z.number(),
        bottomLeft: z.number(),
    })
        .optional(),
    backgroundColor: z.string().optional(),
    /**
     * CSS `mix-blend-mode` for a Figma layer blend that is not NORMAL. Absent =
     * normal compositing, which is what the compiler has always done, so every
     * definition written before this field renders byte-identically.
     *
     * A blend cannot be baked into a per-node raster, because it is a function of
     * what lies BEHIND the node — the exported PNG carries the layer's own opaque
     * pixels. Live 2026-08-15: the AJet 120x600 master's cloud headline is LIGHTEN
     * in Figma, where its opaque blue plate disappears against the sky; without
     * the mode it shipped as a flat blue rectangle with a visible seam.
     */
    blendMode: z.string().optional(),
    // Epic 20 Phase 01 — structured drop shadow (NOT a raw CSS string: safety +
    // addressability). Renders as `box-shadow: x y blur spread color`.
    boxShadow: z
        .object({
        x: z.number(),
        y: z.number(),
        blur: z.number(),
        spread: z.number(),
        color: z.string(),
    })
        .optional(),
    border: z.string().optional(),
    overflow: z.enum(['visible', 'hidden']).optional(),
});
const textRunSchema = z.object({
    text: z.string(),
    fontWeight: z.number().int().optional(),
    color: z.string().optional(),
});
const textPropertiesSchema = z.object({
    content: z.string(),
    fontFamily: z.string(),
    fontSize: z.number().positive(),
    fontWeight: z.number().int(),
    color: z.string(),
    lineHeight: z.number().optional(),
    letterSpacing: z.number().optional(),
    textAlign: z.enum(['left', 'center', 'right']).optional(),
    // Where the text sits INSIDE its own box, from Figma's `textAlignVertical`.
    // Absent = top, which is what the renderer has always done — so every
    // existing definition parses and compiles byte-identically.
    verticalAlign: z.enum(['center', 'bottom']).optional(),
    textDecoration: z.enum(['none', 'underline', 'line-through']).optional(),
    // Epic 20 Phase 01 — italic toggle (additive; default normal).
    fontStyle: z.enum(['normal', 'italic']).optional(),
    autoWidth: z.boolean().optional(),
    runs: z.array(textRunSchema).optional(),
    // 2026-08-27 — the glyphs' own painted rectangle, fractions of the element's own
    // frame. Declared here for the same reason every optional field on this file's
    // schemas is: `z.object` STRIPS what it does not declare, so a field the type
    // carries and the schema does not is silently dropped on every save. See
    // `TextProperties.inkBox` (types/element.ts) for the live batch it closes.
    inkBox: z
        .object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() })
        .optional(),
});
const assetAdaptationSchema = z.object({
    action: z.enum([
        // LIVE ops (still emitted): the non-AI/degraded safe ops, the gpt reframe /
        // extend→re-cutout tiers, the local-matte remove-bg, and the gpt-image-2 edit.
        'sharp-downscale',
        'sharp-smartcrop',
        // A cut-out (logo/icon/product on transparency) fitted WHOLE into its box —
        // never centre-cropped (the logo-cut fix). See `element.ts` `AssetAdaptation`.
        'sharp-contain',
        // 2026-09-06 — a soft SHADE (alpha fade, one ink, no edge) stretched to its box
        // so its corner stays in the corner. See `element.ts` `AssetAdaptation`.
        'sharp-stretch',
        'ai-recompose',
        'ai-recompose-cutout',
        'ai-remove-bg',
        // gpt-image-2 prompt-driven edit of a composed plate (İçerik, image-first).
        'ai-gpt-edit',
        // LEGACY read-only (Epic 33 Phase 06): executors deleted with the pre-Epic-33
        // image provider (superres / outpaint / search-replace / search-recolor), NOT
        // replaced; retained so pre-Epic-33 rows still parse (required field). Not emitted.
        'ai-superres',
        'ai-outpaint',
        'ai-search-replace',
        'ai-search-recolor',
    ]),
    provider: z.string().optional(),
    ratioSignature: z.string(),
    // Epic 15 planner provenance — declared so it round-trips zod parse
    // (z.object strips unknown keys). Optional/additive: legacy rows omit it.
    treatment: z.enum(['extend', 'reframe', 'crop', 'scale', 'leave']).optional(),
    // Epic 19/21 content-edit provenance — same round-trip rationale. Present only on
    // content-mode adaptations. Op NAME only (the generative ops' params are transient).
    contentOp: z
        .enum([
        // LIVE content ops (all-OpenAI vocabulary).
        'remove-bg',
        'extend',
        // Sprint 044 — deletion/instance-reduction (executes via gpt-edit → ai-gpt-edit).
        'remove-object',
        'gpt-edit',
        // The PLANNER-offered counterpart of `gpt-edit`: any faithful in-place pixel
        // change no other op names (sharpen/upscale, colour or material change, object
        // swap, un-crop). Executes via the gpt-image-2 tier carrying the planner's
        // English restatement — see `adaptation/content-op.ts`.
        'pixel-edit',
        'leave',
        // LEGACY read-only (Epic 33 Phase 06): the pre-Epic-33 content ops
        // (uncrop/superres/search-replace/search-recolor) were deleted, NOT replaced;
        // retained so pre-Epic-33 content-mode rows still parse. No longer emitted.
        'uncrop',
        'superres',
        'search-replace',
        'search-recolor',
    ])
        .optional(),
    // Epic 21 P02 — content-edit outcome + human-readable reason (SC-5 honest report).
    // `applied` succeeded (+ passed critique when on); `leave` = out of scope;
    // `rejected` = attempted but failed the identity/artifact gate (original kept).
    contentOutcome: z.enum(['applied', 'leave', 'rejected']).optional(),
    contentReason: z.string().optional(),
    // Epic 33 Phase 04 — the İçerik re-strip's LOCAL matte degraded (BiRefNet →
    // u2netp, or no engine). Honest cut-out-quality badge. Optional/additive.
    matteDegraded: z.boolean().optional(),
    // 2026-09-03 — this layer shares a box with `coframedWith` in the master (a cut-out
    // over its own photograph). Visible: it was framed onto THAT layer's frame instead of
    // being adapted on its own, so the two register as in the master. `hidden: true` on the
    // element: that layer ships from an outpaint plate, which no overlay can register to,
    // so this one was hidden rather than shown doubled.
    coframedWith: z.string().optional(),
    // Epic 29 Phase 02 — face-aware reframe verdict (Phase 04 gate + diagnostics).
    // Declared so it round-trips zod parse; optional/additive: legacy rows omit it.
    // 2026-07-28: the shape declared here used to stop at `facesFit` + `window`, so a
    // parse silently DROPPED `facesContained`, `mode`, `fillSubset` and `faceBoxes` —
    // the only record of where the people ended up. Every field the generator stamps is
    // declared now, or the guards that read them see a picture with nobody in it.
    // `facesFit` became optional the same day: it is a measurement, and a placement made
    // without face data has no answer to give (see `AssetAdaptation['faceSafety']`).
    faceSafety: z
        .object({
        facesFit: z.boolean().optional(),
        facesContained: z.boolean().optional(),
        mode: z.enum(['crop', 'subject-fit']).optional(),
        fillSubset: z.boolean().optional(),
        // Optional since 2026-07-28: a record written because detection found NOBODY
        // has face facts but no face-safe placement, so there is no window to report.
        window: z
            .object({
            left: z.number(),
            top: z.number(),
            width: z.number(),
            height: z.number(),
        })
            .optional(),
        faceBoxes: z
            .array(z.object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() }))
            .optional(),
        faceBoxesStatus: z.enum(['measured', 'unknown']).optional(),
        // 2026-09-10 — what the picture is OF when nobody is in it (`subjectBoxes`).
        // Declared here for the same reason the 2026-07-28 note above gives: a field the
        // generator stamps but this schema does not name is silently dropped on parse,
        // and this one is the only record a faceless subject leaves behind.
        subjectBoxes: z
            .array(z.object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() }))
            .optional(),
    })
        .optional(),
    // Epic 29 Phase 03 — text-over-photo legibility verdict (Phase 04 gate +
    // evidence). Same round-trip rationale; optional/additive: legacy rows omit it.
    // `contrast` is finite in practice (a text with no photo under it is excluded
    // upstream), but Infinity would not survive JSON anyway — the generator rounds.
    legibility: z
        .object({
        legible: z.boolean(),
        texts: z.array(z.object({ id: z.string(), contrast: z.number(), pass: z.boolean() })),
        scrimHold: z.number(),
        // Sprint 042 #5 — per-image-overlay legibility evidence (measurement-only,
        // telemetry). optional/additive: legacy rows and text-only verdicts omit it.
        images: z
            .array(z.object({ id: z.string(), contrast: z.number(), pass: z.boolean() }))
            .optional(),
    })
        .optional(),
    // Epic 29 Phase 04 — reframe composition-adequacy evidence (thin-sliver
    // detection for the quality gate). Same round-trip rationale; optional/additive.
    reframe: z
        .object({
        visibleFraction: z.number(),
        constrained: z.boolean(),
        compositionAdequate: z.boolean(),
    })
        .optional(),
});
/**
 * Every enum in this file that has a hand-maintained TS union counterpart, reflected
 * read-only so the union⇄enum drift pin can compare them (Epic 34 Phase 00 — these enums
 * are the ONLY runtime gate). Reflection, NOT a second source of truth: the enums above
 * stay canonical. Package-internal — `index.ts` re-exports an explicit named list, so
 * these are not part of banner-core's public API.
 *
 * The list is exhaustive by construction, not by inspection. Twice, a hand diff declared
 * this file clean and missed a pair: `sharp-contain` (action) and `pixel-edit` (contentOp)
 * were both emittable-but-unparseable — the generator wrote them, the renderer 400'd.
 * `Treatment` and `ContentOp` derive from runtime arrays (`TREATMENTS` / `CONTENT_OPS`), so
 * their pins compare against those; the rest are hand unions and are pinned via keyed maps.
 */
export const ASSET_ADAPTATION_ACTIONS = assetAdaptationSchema.shape.action.options;
export const ASSET_ADAPTATION_TREATMENTS = assetAdaptationSchema.shape.treatment.unwrap().options;
export const ASSET_ADAPTATION_CONTENT_OPS = assetAdaptationSchema.shape.contentOp.unwrap().options;
const focalPointSchema = z.object({ x: z.number(), y: z.number() });
const assetReferenceSchema = z.object({
    s3Key: z.string(),
    url: z.string(),
    // Owner request 2026-08-05 — the pristine originally-uploaded bytes, captured
    // alongside (never instead of) s3Key/url above. Declared so it round-trips zod
    // parse (z.object strips unknown keys) instead of being silently dropped — the
    // exact inkBox-class bug this file's own inkBox comment (below) already warns
    // about. See types/element.ts's AssetReference doc for the full rationale.
    rawS3Key: z.string().optional(),
    rawUrl: z.string().optional(),
    // Owner request 2026-08-05 — whether `rawUrl` is safe to render as the
    // default source (Figma FILL-mode's crop math matches CSS object-fit:cover
    // exactly, or CROP-mode's captured transform was axis-aligned). See
    // types/element.ts's AssetReference doc for the full rationale.
    rawPanSafe: z.boolean().optional(),
    // DBG-034 BUG-21 (2026-08-05) — CROP-mode's raw-image crop window
    // (fractions of the raw image). See types/element.ts's doc. Declared so it
    // round-trips zod parse instead of being silently dropped.
    rawCropWindow: z
        .object({ left: z.number(), top: z.number(), width: z.number(), height: z.number() })
        .optional(),
    originalFilename: z.string().optional(),
    mimeType: z.string(),
    width: z.number().positive().optional(),
    height: z.number().positive().optional(),
    autoScaled: z.boolean().optional(),
    // Epic 12 adaptation provenance — additive/optional so master + legacy
    // definitions validate unchanged. Declared so it round-trips zod parse
    // (z.object strips unknown keys) instead of being silently dropped.
    adaptation: assetAdaptationSchema.optional(),
    // Epic 14 (anchor reflow) responsive image hints — additive/optional.
    naturalAspect: z.number().positive().optional(),
    focal: focalPointSchema.optional(),
    objectFit: z.enum(['cover', 'contain']).optional(),
    // Epic 44 Phase 02 (task 2.2) — user-driven pan (Decision 0.3: pan = this field;
    // zoom = a position.width/height box resize, not a field here). CSS percent (0-100)
    // of the box — NOT `focal` above, which is 0-1 of the asset and read only at
    // generation time by layout/anchor.ts. Declared so it round-trips zod parse
    // (z.object strips unknown keys) instead of being silently dropped.
    objectPosition: z.object({ x: z.number(), y: z.number() }).optional(),
    // Phase 14 (Epic 44, 2026-08-05) — who set objectPosition ('ai-crop' = the
    // generator, reproducibly, against rawUrl). See types/element.ts's doc. Declared
    // so it round-trips zod parse instead of being silently dropped.
    objectPositionSource: z.literal('ai-crop').optional(),
    // Declared so it round-trips a zod parse (2026-08-27). This object strips what it
    // does not name, and the export, render and optimize handlers all rebuild a
    // definition through `parse` before compiling the HTML they ship — an undeclared
    // field is silently gone by then, which is exactly how `inkBox` and `rawCropWindow`
    // were lost before. See `AssetReference.modelZoomWindow`.
    modelZoomWindow: z
        .object({
        renderWidth: z.number().finite().positive(),
        renderHeight: z.number().finite().positive(),
        offsetX: z.number().finite(),
        offsetY: z.number().finite(),
    })
        .optional(),
    // Epic 44 Phase 02 (task 2.2b) — closes a live data-loss bug, not new scope. Matches
    // the type at types/element.ts:357. Written by services/generator/src/assets.ts:2955
    // for every non-fully-opaque adapted asset; read by layout/check.ts:938
    // (inkBoxOnCanvas) to score face coverage on PAINTED pixels rather than the box.
    // Measured 2026-08-04: 48 elements in the live dev DB carry it today. It has been in
    // the type since it was added and absent here the whole time, so every
    // PUT .../banners/:id/definition save silently stripped it. Declared so it round-trips
    // zod parse (z.object strips unknown keys) instead of being silently dropped.
    inkBox: z
        .object({ x: z.number(), y: z.number(), width: z.number(), height: z.number() })
        .optional(),
    // 2026-08-17 — measured per-pixel opacity, the fact the occlusion guard needs
    // and banner-core cannot derive. Declared here for the same reason inkBox is:
    // z.object strips unknown keys, so an undeclared field is silently dropped on
    // every definition save. See types/element.ts's AssetReference doc for the
    // live campaign this closes.
    opaqueRaster: z.boolean().optional(),
});
const shapePropertiesSchema = z.object({
    type: z.enum(['rectangle', 'ellipse', 'polygon']),
    fill: z.string().optional(),
    stroke: z.string().optional(),
    strokeWidth: z.number().optional(),
});
const elementAnchorSchema = z.object({
    h: z.enum(['left', 'right', 'center', 'stretch']),
    v: z.enum(['top', 'bottom', 'center', 'stretch']),
});
const sizeModeSchema = z.enum(['fixed', 'scale', 'stretch']);
const elementSizeBehaviorSchema = z.object({ w: sizeModeSchema, h: sizeModeSchema });
// Epic 51 Phase 04 (task 04.2) — sentence membership + reading order, derived
// from the master itself (`ElementSentenceRelation`'s own doc, `types/element.ts`,
// has the full rationale). Declared so it round-trips zod parse (z.object strips
// unknown keys) instead of being silently dropped on every definition save/read —
// the exact class of bug `inkBox`/`opaqueRaster`/`faceSafety` above already name
// live incidents for.
const elementSentenceRelationSchema = z.object({
    sentenceId: z.string(),
    readingOrder: z.number().int().nonnegative(),
});
const elementRoleSchema = z.enum([
    'background',
    'headline',
    'subhead',
    'cta',
    'logo',
    'product-image',
    'decorative',
    'body',
    'badge',
]);
const bannerElementSchema = z.object({
    id: z.string(),
    // Optional so legacy/imported definitions (pre-numbering) still validate;
    // `assignElementNumbers` backfills it (Epic 11 P00).
    number: z.number().int().positive().optional(),
    type: z.enum(['image', 'text', 'shape']),
    name: z.string().optional(),
    position: positionSchema,
    styles: elementStylesSchema,
    text: textPropertiesSchema.optional(),
    asset: assetReferenceSchema.optional(),
    shape: shapePropertiesSchema.optional(),
    // Epic 14 (anchor reflow) responsive model — additive/optional.
    anchor: elementAnchorSchema.optional(),
    sizeBehavior: elementSizeBehaviorSchema.optional(),
    sectionId: z.string().optional(),
    groupId: z.string().optional(),
    role: elementRoleSchema.optional(),
    // Epic 51 Phase 04 (task 04.2) — see `elementSentenceRelationSchema`'s own
    // comment, immediately above, for why this must be declared here.
    sentence: elementSentenceRelationSchema.optional(),
    // Epic 17 Phase 05 — user-directed drop (hide-not-delete). Additive/optional.
    hidden: z.boolean().optional(),
});
// The non-provenance union⇄enum pairs — same shape and same failure mode as `action`,
// previously unguarded. Declared here because they depend on the schemas above.
export const ELEMENT_ROLES = elementRoleSchema.options;
export const ELEMENT_SIZE_MODES = sizeModeSchema.options;
export const ELEMENT_TYPES = bannerElementSchema.shape.type.options;
// --- Animation schemas ---
const animationTimingSchema = z.object({
    delay: z.number().min(0),
    duration: z.number().positive(),
    easing: z.string(),
    // Loop support (Sprint 047). Optional, so every definition authored before it
    // existed still validates and still compiles to the same single-shot CSS.
    iterations: z.union([z.number().positive(), z.literal('infinite')]).optional(),
    direction: z.enum(['normal', 'alternate']).optional(),
});
const animationPropertyStateSchema = z.record(z.string(), z.number().optional());
const animationSchema = z.object({
    id: z.string(),
    targetElement: z.string(),
    scene: z.string(),
    type: z.string(),
    timing: animationTimingSchema,
    from: animationPropertyStateSchema,
    to: animationPropertyStateSchema,
    // Optional preset provenance (Epic 11 P01) — tolerant of absence so Epic 05
    // definitions (no preset linkage) still validate.
    presetSignature: z.string().optional(),
});
// --- Structured animation (Epic 11) ---
const animationInstructionParamsSchema = z.object({
    direction: z.enum(['up', 'down', 'left', 'right']).optional(),
    from: animationPropertyStateSchema.optional(),
    to: animationPropertyStateSchema.optional(),
    durationMs: z.number().positive().optional(),
    delayMs: z.number().min(0).optional(),
    easing: z.string().optional(),
});
export const animationInstructionSchema = z.object({
    elementNumber: z.number().int().positive(),
    action: z.string(),
    // The translator is told (and the params interface is built) to leave unused
    // params unset — and Claude routinely omits the object entirely for a bare
    // action like "fade in element 1". Default to `{}` so a params-less
    // instruction validates; `signatureOf`/resolution already treat every param
    // as absent-safe (Epic 11 P04 live finding).
    params: animationInstructionParamsSchema.default({}),
});
export const animationPresetSchema = z.object({
    type: z.string(),
    from: animationPropertyStateSchema,
    to: animationPropertyStateSchema,
    timing: animationTimingSchema,
    params: z.record(z.string(), z.union([z.number(), z.string()])).optional(),
});
/** Pool of reusable presets keyed by signature (Epic 11 P01 reuse-not-regenerate). */
export const presetCatalogSchema = z.record(z.string(), animationPresetSchema);
// --- Scene schemas ---
const transitionConfigSchema = z.object({
    type: z.enum(['none', 'fade', 'slide', 'dissolve']),
    duration: z.number().min(0),
    easing: z.string().optional(),
});
const elementStateOverrideSchema = z.object({
    elementId: z.string(),
    position: z
        .object({
        x: z.number().optional(),
        y: z.number().optional(),
        width: z.number().optional(),
        height: z.number().optional(),
    })
        .optional(),
    styles: z
        .object({
        opacity: z.number().optional(),
        transform: z.string().optional(),
    })
        .optional(),
    properties: animationPropertyStateSchema.optional(),
});
const sceneSchema = z.object({
    id: z.string(),
    name: z.string().optional(),
    duration: z.number().positive(),
    visibleElements: z.array(z.string()),
    elementStates: z.array(elementStateOverrideSchema),
    transition: transitionConfigSchema,
});
// --- Top-level schemas ---
const canvasSchema = z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    backgroundColor: z.string(),
});
const interactionsSchema = z.object({
    clickUrl: z.string().optional(),
    clickTarget: z.enum(['_blank', '_self']).optional(),
    hoverStates: z
        .array(z.object({
        elementId: z.string(),
        styles: z.object({
            opacity: z.number().optional(),
            transform: z.string().optional(),
            cursor: z.string().optional(),
        }),
    }))
        .optional(),
});
const constraintsSchema = z.object({
    maxDuration: z.number().positive().optional(),
    maxLoops: z.number().int().positive().optional(),
    maxFileSize: z.number().int().positive().optional(),
    requireClickTag: z.boolean().optional(),
});
/** Second of the THREE declarations `AnimationVariant` needs — see the type's own note. Without
 *  this line the field is silently stripped by `PUT .../banners/:id/definition`, which validates
 *  the whole body against the schema below, and a saved variant would vanish on the round trip. */
const animationVariantSchema = z.object({
    id: z.string(),
    name: z.string(),
    animations: z.array(animationSchema),
});
export const bannerDefinitionSchema = z.object({
    canvas: canvasSchema,
    elements: z.array(bannerElementSchema),
    scenes: z.array(sceneSchema),
    animations: z.array(animationSchema),
    interactions: interactionsSchema,
    constraints: constraintsSchema,
    animationVariants: z.array(animationVariantSchema).optional(),
    activeAnimationVariantId: z.string().optional(),
});
const renderedBannerMetadataSchema = z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    fileSize: z.number().int().nonnegative(),
    format: z.enum(['html5', 'gif', 'png', 'jpg']),
    sceneCount: z.number().int().nonnegative(),
    duration: z.number().min(0),
    generatedAt: z.string().datetime(),
});
export const renderedBannerSchema = z.object({
    html: z.string(),
    assets: z.array(assetReferenceSchema),
    metadata: renderedBannerMetadataSchema,
});
